//jsxhook

import {
  enterObjectTransitionProperties,
  FormVariants,
  HeadingRegularVariants,
  HollowLightButton,
  isTouchDevice,
  overLayMotion,
  PrimaryButton,
  zIndices
} from "@sixty-six-north/ui-system"
import { createSearchLink } from "components/Links"
import { Field, Form, Formik, FormikHelpers } from "formik"
import { AnimatePresence, m } from "framer-motion"
import { useStoreContext } from "i18n/StoreHooks"
import { useLayout } from "layout/LayoutState"
import { useRouter } from "next/router"
import { OrderBy } from "product/ProductDalTypes"
import React, { useCallback, useEffect, ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { useSearchQuery } from "search/SearchQueryContext"
import { Box, Flex, Input as ThemeInput } from "theme-ui"
import * as Yup from "yup"
import { useAppDispatch } from "../../../State"

interface FormData {
  query: string
}

const validate = (t: (error: string) => string) => {
  return Yup.object().shape({
    query: Yup.string().required(t("queryIsRequired"))
  })
}

// force open keyboard for iOS devices when focusing on the search input
const focusAndOpenKeyboard = (el, timeout = 800) => {
  if (el) {
    // Align temp input element approximately where the input element is
    // so the cursor doesn't jump around
    const tempEl = document.createElement("input")
    tempEl.style.position = "absolute"
    tempEl.style.top = el.offsetTop + 7 + "px"
    tempEl.style.left = el.offsetLeft + "px"
    tempEl.style.height = "0"
    tempEl.style.opacity = "0"
    // Put this temp element as a child of the page <body> and focus on it
    document.body.appendChild(tempEl)
    tempEl.focus()

    // The keyboard is open. Now do a delayed focus on the target element
    setTimeout(function () {
      el.focus()
      el.click()
      // Remove the temp element
      document.body.removeChild(tempEl)
    }, timeout)
  }
}

interface SearchInputProps {
  placeholder: string
  ourRef: React.RefObject<HTMLInputElement>
  children?: ReactNode
}

export const SearchInput: React.FC<SearchInputProps> = ({
  placeholder,
  children,
  ourRef,
  ...props
}) => {
  const { t } = useTranslation("search")
  return (
    <ThemeInput
      ref={ourRef}
      placeholder={placeholder || t("search66North")}
      variant={FormVariants.search}
      {...props}
    />
  )
}

export const SearchBar: React.FC<{
  show: boolean
  query?: string
  onSearch: (data: FormData) => Promise<unknown>
  onHideSearch: () => Promise<unknown>
}> = ({ show, query, onHideSearch, onSearch }) => {
  const { t } = useTranslation("search")
  const ref = useCallback(node => {
    if (node && show) {
      if (isTouchDevice()) {
        focusAndOpenKeyboard(node)
      } else {
        node.focus()
      }
    }
  }, [])

  const onSubmit = (
    values: FormData,
    helpers: FormikHelpers<FormData>
  ): Promise<unknown> => {
    helpers.setSubmitting(true)
    return onSearch(values).then(() => onHideSearch())
  }

  useEffect(() => {
    const escapeKeyListener = (ev: KeyboardEvent) => {
      if (show && ev.key === "Escape") {
        onHideSearch()
      }
    }
    if (typeof window !== "undefined") {
      window.document.addEventListener("keydown", escapeKeyListener)
    }

    return () => {
      if (typeof window !== "undefined") {
        window.document.removeEventListener("keydown", escapeKeyListener)
      }
    }
  }, [show])

  return (
    <Flex
      sx={{
        flexDirection: "row",
        bg: "grey.4",
        flex: 1,
        py: 24,
        pl: 24,
        pr: 8
      }}
    >
      <Formik
        initialValues={{ query: query || "" }}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validationSchema={validate(t)}
      >
        <Form style={{ flex: 1 }}>
          <Flex
            sx={{
              flexDirection: "row",
              flex: 1
            }}
          >
            <Field
              as={SearchInput}
              name="query"
              sx={{
                flex: 1,
                width: "auto",
                variant: `text.${HeadingRegularVariants.sixth}`,
                textAlign: "left",
                bg: "grey.4",
                p: 0
              }}
              ourRef={ref}
            >
              {t("enterYourSearch", { defaultValue: "Enter your search" })}
            </Field>
            <PrimaryButton type="submit">
              {t("search", { defaultValue: "Search" })}
            </PrimaryButton>
          </Flex>
        </Form>
      </Formik>
      <HollowLightButton
        onClick={ev => {
          ev.preventDefault()
          onHideSearch()
        }}
      >
        {t("forms:cancel", { defaultValue: "ca" })}
      </HollowLightButton>
    </Flex>
  )
}

export const Search: React.FC = ({}) => {
  const searchQuery = useSearchQuery()
  const { country } = useStoreContext()
  const layout = useLayout()
  const { searchOpen } = layout.state
  const router = useRouter()
  return (
    <Box
      sx={{
        position: "fixed",
        top: "0%",
        left: "0%",
        right: "0%",
        zIndex: [zIndices.search],
        display: "block",
        bg: "grey.4",
        width: "100vw"
      }}
    >
      <AnimatePresence>
        {searchOpen && (
          <m.div
            key={`search-overlay`}
            style={{
              position: "fixed"
            }}
            {...overLayMotion}
          >
            <m.div
              hidden={!searchOpen}
              id="search-list"
              key={`search-bar`}
              initial={{
                y: -66,
                opacity: 0
              }}
              animate={{
                y: 0,
                opacity: 1,
                transition: enterObjectTransitionProperties
              }}
              exit={{
                y: -66,
                opacity: 0,
                transition: enterObjectTransitionProperties
              }}
            >
              <Flex
                sx={{
                  width: "100vw",
                  justifyContent: "center",
                  bg: "grey.4"
                }}
              >
                <Flex
                  sx={{ flex: 1, maxWidth: [null, null, null, "75%", "66%"] }}
                >
                  <SearchBar
                    show={searchOpen}
                    query={searchQuery.query || ""}
                    onHideSearch={() => {
                      return Promise.resolve(layout.toggleSearch())
                    }}
                    onSearch={data => {
                      const { href, as } = createSearchLink(
                        data.query,
                        country,
                        OrderBy.priceAsc
                      )
                      return router.push(href, as).finally(() => true)
                    }}
                  />
                </Flex>
              </Flex>

              <Flex
                onClick={() => layout.toggleSearch()}
                sx={{
                  flex: 1,
                  height: "100%",
                  position: "absolute",
                  width: "100%"
                }}
              ></Flex>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </Box>
  )
}
